<template>
  <div class="row">
    <div class="col-12">
      <page-header :actions="headerActionsConfig" :page="headerConfig" />
    </div>
    <div class="col-12">
      <board :details="companyBoardDetails">
        <template #data-item="{ item }">
          <em
            v-if="hasUnMaskSSNPermission && item.isSSNPatronal"
            class="fas ml-1"
            :class="[ssnMasked]"
            role="button"
            @click="toggleSSNMaskHandler()"
          />
        </template>
      </board>
    </div>
    <div class="col-12">
      <validation-observer :ref="mainFormName" v-slot="{}" tag="form">
        <general-info
          v-model="model.generalInfo"
          class="my-5"
          :initial-medicaid-due-date="initialMedicaidDueDate"
          :initial-medical-identifier="initialMedicalIdentifier"
          :initial-npi="initialNpi"
          :initial-npi-depr="initialNpiDepr"
          :is-location-type-school="isLocationTypeSchool"
          :is-medicaid-documentation-workflow-enabled="
            isMedicaidDocumentationWorkflowEnabled
          "
          @save-npi="saveNpi"
        />
        <demographic-info
          v-model="model.demographicInfo"
          class="my-5"
          :home-municipalities="homeMunicipalities"
          :home-municipalities-error.sync="homeMunicipalitiesError"
          :is-read-only="isReadOnly"
          :location-type-error.sync="locationTypeError"
          :location-types="requestInfo.locationTypes"
          :municipalities="municipalities"
          @on-school-municipality="onSelectSchoolMunicipality"
          @on-select-municipality="onSelectMunicipality"
          @on-selected-location-type="onSelectedLocationType"
          @setPhysicalAddress="onSetPhysicalAddress"
        />
        <div class="my-5">
          <addresses
            v-if="isReadOnly"
            :data="model"
            :hidden="isLocationTypeHome"
            :is-provider-office="isProviderOfficeOrSchool"
            :items="model.addresses"
            :location-id="locationId"
            :location-type="model.demographicInfo.locationTypeId"
            :municipalities="requestInfo.municipalities"
            :municipality-id="municipalityId"
            @reload="getDetailInfo(locationFiscalYearId)"
          />
          <physical-address-info
            v-else
            v-model="model.physicalAddressInfo"
            :hidden="isLocationTypeHome"
            :is-read-only="isReadOnly"
            :municipality="municipalitySelected"
          />
        </div>
        <div class="mt-5">
          <contacts
            v-if="isReadOnly"
            :items="model.contacts"
            :location-id="locationId"
            @reload="onLoad"
          />
          <contact-info
            v-else
            v-model="model.contactInfo"
            :is-read-only="isReadOnly"
          />
        </div>
        <div v-if="isReadOnly">
          <div
            v-if="!isOpenAddOrEditContact"
            class=" d-flex flex-row-reverse mt-3"
          >
            <action-button
              i="fas fa-file-plus"
              text="Añadir"
              variant="success"
              @click="onAddOrEditContactHandler()"
            />
          </div>
          <div v-else class="bg-light-gray rounded py-1 mt-3">
            <div class="row m-2">
              <add-contact
                v-model="model.secondaryContact"
                :location-id="locationId"
                @cancel="cancelAddorEditContact()"
                @save="saveAddorEditContact()"
              />
            </div>
          </div>
        </div>
        <div v-if="isReadOnly && canShowPublicPortalSection" class="mt-5">
          <public-portal-info
            v-model="model.generalInfo"
            :associated-disciplines="model.associatedDisciplines"
            :show-error.sync="yesNoError"
          />
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import Board from 'Board';
import { ValidationObserver } from 'vee-validate';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import CodeLocationTypes from '@/utils/constants/codeLocationTypes';
import AddressTypes from '@/utils/constants/addressTypes.js';
import { getByParameterName } from '@/services/common/sysparameter.api';
import {
  getRequestinfo as _getRequestinfo,
  getDetailInfo as _getDetailInfo,
  saveLocationService as _saveLocationService,
  deleteLocationServiceContact as _deleteLocationServiceContact,
  editGeneralInformation as _editGeneralInformation,
  updateLocationServiceNPI as _updateLocationServiceNPI,
  hasactivespecialists as _hasactivespecialists,
  deactivateLocationService as _deactivateLocationService,
  activateLocationService as _activateLocationService,
} from '@/services/provider/location-service.api.js';
import { getSsn as _getSsn } from '@/services/provider/provider.api.js';
import {
  LocationServiceGeneralInfo as GeneralInfo,
  LocationServiceDemographicInfo as DemographicInfo,
  LocationServicePhysicalAddressInfo as PhysicalAddressInfo,
  LocationServiceContactInfo as ContactInfo,
  LocationServicePublicPortalInfo as PublicPortalInfo,
  LocationServiceAddresses as Addresses,
  LocationServiceContacts as Contacts,
  AddLocationServiceContact as AddContact,
} from './components';

const LOCATION_SERVICE_MANAGMENT_ROUTE_NAME = 'locationServiceManagement';

export default {
  name: 'LocationServiceManagement',
  components: {
    PageHeader,
    Board,
    ValidationObserver,
    GeneralInfo,
    PhysicalAddressInfo,
    DemographicInfo,
    ContactInfo,
    PublicPortalInfo,
    Addresses,
    Contacts,
    AddContact,
    ActionButton,
  },
  mixins: [alertMixin],
  data: () => ({
    isMedicaidDocumentationWorkflowEnabled: false,
    initialNpi: null,
    initialNpiDepr: null,
    initialMedicalIdentifier: null,
    initialMedicaidDueDate: null,
    model: {
      locationId: null,
      locationFiscalYearId: null,
      isActive: null,
      generalInfo: {
        websiteUrl: null,
        phone: null,
        fax: null,
        locationAvailableFlag: null,
        locationSchoolNationalIdentifier: null,
        nationalIdentifier: null,
        medicalIdentifier: null,
        medicaidDueDate: null,
      },
      physicalAddressInfo: {
        id: null,
        addressTypeCd: AddressTypes.Physical,
        addressLine1: null,
        addressLine2: null,
        addressLine3: null,
        municipalityId: null,
        zipCode: null,
        latitude: null,
        longitude: null,
        additionalAnnotations: null,
      },
      demographicInfo: {
        locationTypeId: null,
        regionId: null,
        schoolTypeId: null,
        name: null,
        municipalityId: null,
        locationSchoolId: null,
        centerId: null,
        providerName: null,
        municipalities: [],
      },
      contactInfo: {
        id: null,
        nameFirst: null,
        nameLastPaternal: null,
        nameLastMaternal: null,
        department: null,
        position: null,
        email: null,
        cellPhone: null,
        homePhone: null,
        workPhone: null,
        workPhoneExt: null,
        fax: null,
        isPrimaryContactFlag: false,
      },
      secondaryContact: {
        locationId: null,
        isPrimaryContactFlag: null,
        name: null,
        initial: null,
        lastName: null,
        secondSurname: null,
        workDepartmentName: null,
        workPositionTitle: null,
        email: null,
        telephoneMobileNo: null,
        telephoneWorkNo: null,
        workPhoneExt: null,
        telephoneWorkFaxNo: null,
        telephoneHomeNo: null,
      },
      addresses: [],
      contacts: [],
      associatedDisciplines: [],
    },
    requestInfo: {
      companyInfo: {},
      locationTypes: [],
      municipalities: [],
      homeMunicipalitiesToExclude: [],
    },
    centerAddress: {},
    homeMunicipalitiesError: false,
    locationTypeError: false,
    isOpenAddOrEditContact: false,
    isSsnMasked: true,
    isProviderOfficeOrSchool: false,
    municipalitySelected: null,
    yesNoError: false,
  }),
  computed: {
    mainFormName() {
      return 'providerLocationServiceMainForm';
    },
    headerConfig() {
      return {
        icon: 'fas fa-clipboard-list',
        title: 'Añadir Lugares de Servicios',
        routes: [
          'Inicio',
          'Proveedores',
          'Lugares de Servicio',
          this.routeName,
        ],
      };
    },
    routeName() {
      return this.isReadOnly ? 'Ver' : 'Añadir';
    },
    headerActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left fa-fw',
          variant: 'outline-success',
          click: () => this.$router.go(-1),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save pr-2',
          variant: 'success',
          click: () => this.onSubmitHandler(),
        },
        {
          text: 'Inactivar',
          icon: 'fas fa-user-alt-slash fa-fw',
          variant: 'outline-success',
          visible: this.showInactivate,
          click: () => this.onInactivateHandler(),
        },
        {
          text: 'Re-Activar',
          icon: 'fas fa-user-check',
          variant: 'success px-1',
          visible: this.showActivate,
          click: () => this.onActiveHandler(),
        },
      ];
    },
    companyBoardDetails() {
      return [
        [
          {
            label: 'Año Fiscal:',
            data: this.requestInfo?.companyInfo?.academicYearDescription,
            icon: 'fas fa-calendar-check',
            cols: 'col-lg-2 pt-2 pl-5',
          },
          {
            label: 'Tipo de Proveedor:',
            data: this.requestInfo?.companyInfo?.providerTypeName,
            icon: 'fas fa-ballot-check',
            cols: 'col-lg-2 pt-2',
          },
          {
            label: 'Nombre de la Compañia:',
            data: this.requestInfo?.companyInfo?.providerName,
            icon: 'fas fa-address-book',
            cols: 'col-lg-5 pt-2',
          },
          {
            isSSNPatronal: true,
            label: 'SSN Patronal:',
            data: this.requestInfo?.companyInfo?.ssn,
            icon: 'fas fa-address-book',
            cols: 'col-lg-3 pt-2',
          },
        ],
      ];
    },
    isReadOnly() {
      return Boolean(this.locationFiscalYearId);
    },
    companyId() {
      return this.$route.params.companyId
        ? Number.parseInt(this.$route.params.companyId)
        : null;
    },
    locationFiscalYearId() {
      return this.$route.params.locationFiscalYearId
        ? Number.parseInt(this.$route.params.locationFiscalYearId)
        : null;
    },
    locationId() {
      return this.model.locationId;
    },
    isLocationTypeHome() {
      return (
        this.model.demographicInfo.locationTypeId === CodeLocationTypes.Home
      );
    },
    isLocationTypeSchool() {
      return (
        this.model.demographicInfo.locationTypeId === CodeLocationTypes.School
      );
    },
    isLocationTypeSelected() {
      return Boolean(this.model.demographicInfo.locationTypeId);
    },
    hasUnMaskSSNPermission() {
      return this.userHasPermissions('rempro.providers.display.ssn');
    },
    ssnMasked() {
      return this.isSsnMasked ? 'fa-eye' : 'fa-eye-slash';
    },
    municipalityId() {
      return this.model.demographicInfo.municipalityId;
    },
    isActive() {
      return this.model.isActive;
    },
    showActivate() {
      if (!this.locationFiscalYearId || this.isActive == null) {
        return false;
      }
      return !this.isActive && this.requestInfo?.isCurrentFiscalYear;
    },
    showInactivate() {
      if (!this.locationFiscalYearId || this.isActive == null) {
        return false;
      }
      return this.isActive && this.requestInfo?.isCurrentFiscalYear;
    },
    homeMunicipalities() {
      return [...this.requestInfo.municipalities].filter(
        (x) => !this.requestInfo.homeMunicipalitiesToExclude.includes(x.id)
      );
    },
    municipalities() {
      return this.requestInfo.municipalities;
    },
    canShowPublicPortalSection() {
      return this.userHasPermissions(
        'rempro.providers.locationservice.publicportal'
      );
    },
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.path !== from.path) {
      next();
      await this.onLoad();
    }
  },
  async mounted() {
    await this.onLoad();
    await this.getParams();
  },
  methods: {
    async getParams() {
      const { data } = await getByParameterName(
        'MedicaidDocumentationWorkflowEnabled'
      );

      this.isMedicaidDocumentationWorkflowEnabled =
        data?.value?.toLowerCase() === 'true';
    },
    async saveNpi(model, callback) {
      await this.handleSaveGeneralInfo();
      this.initialNpi = model.nationalIdentifier || '';
      this.initialNpiDepr = model.locationSchoolNationalIdentifier;
      this.initialMedicalIdentifier = model.medicalIdentifier;
      this.initialMedicaidDueDate = model.medicaidDueDate;
      if (callback) callback();
    },
    async handleSaveGeneralInfo() {
      const isValid = await this.$refs[this.mainFormName].validate();
      if (!isValid) return;
      try {
        const payload = {
          locationId: parseInt(this.locationId),
          nationalIdentifier: this.model.generalInfo.nationalIdentifier,
          locationSchoolNationalIdentifier: this.model.generalInfo
            .locationSchoolNationalIdentifier,
          medicaidDueDate: this.model.generalInfo.medicaidDueDate || null,
          medicalIdentifier: this.model.generalInfo.medicalIdentifier || null,
        };
        await _updateLocationServiceNPI(payload);

        this.ShowToast(
          'Proceso Exitoso',
          'Información guardada exitosamente.',
          'success'
        );
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data ||
          error?.message ||
          'Ocurrió un error al guardar la información. Intente nuevamente.';

        this.ShowToast('Error', message, 'error');
      }
    },
    async onLoad() {
      await Promise.all([
        this.getRequestInfo(this.companyId),
        this.getDetailInfo(this.locationFiscalYearId),
      ]);
    },
    async getRequestInfo(companyId) {
      if (!companyId) return;
      const { data } = await _getRequestinfo(companyId);
      this.requestInfo = { ...data };
    },
    async getDetailInfo(locationFiscalYearId) {
      if (!locationFiscalYearId) return;
      const { data } = await _getDetailInfo(locationFiscalYearId);
      this.model = { ...data };
      this.initialNpi = data.generalInfo.nationalIdentifier;
      this.initialNpiDepr = data.generalInfo.locationSchoolNationalIdentifier;
      this.initialMedicalIdentifier = data.generalInfo.medicalIdentifier;
      this.initialMedicaidDueDate = data.generalInfo.medicaidDueDate;
    },
    async onSubmitHandler() {
      if (this.isReadOnly) {
        this.yesNoError = this.model.generalInfo.locationAvailableFlag === null;

        const isFormValid =
          (await this.$refs[this.mainFormName].validate()) && !this.yesNoError;

        if (!isFormValid) {
          this.ShowToast(
            'Alerta',
            'Favor completar la información solicitada.',
            'error'
          );
          return;
        }

        var hasSelectedDisciplines = this.model?.associatedDisciplines?.some(
          (x) => x.selected
        );

        if (
          this.model.generalInfo.locationAvailableFlag === true &&
          !hasSelectedDisciplines
        ) {
          this.ShowToast(
            'Alerta',
            'Favor marcar al menos una disciplina.',
            'error'
          );
          return;
        }

        const { data } = await _editGeneralInformation({
          ...this.model.generalInfo,
          locationId: this.locationId,
          locationFiscalYearId: this.locationFiscalYearId,
          associatedDisciplines: this.model.associatedDisciplines,
        }).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });

        if (data) {
          this.ShowToast(
            'Proceso Exitoso',
            'Los datos han sido guardados exitosamente.',
            'success'
          );
          this.getDetailInfo(this.locationFiscalYearId);
        }
      } else {
        await this.save();
      }
    },
    async save() {
      this.homeMunicipalitiesError =
        this.isLocationTypeHome &&
        this.model.demographicInfo.municipalities.length === 0;

      this.locationTypeError = !this.isLocationTypeSelected;

      const isFormValid = await this.$refs[this.mainFormName].validate();

      if (!isFormValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      const { data } = await _saveLocationService({
        companyId: Number.parseInt(this.companyId),
        ...this.model,
      }).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
        return;
      });

      if (data) {
        this.ShowToast(
          'Proceso Exitoso',
          'El lugar de servicio se ha creado exitosamente.',
          'success'
        );

        this.model.locationId = data.locationId;
        await this.handleSaveGeneralInfo();
        this.loadLocationService(data);
      }
    },
    loadLocationService({ companyId, locationFiscalYearId }) {
      this.$router.replace({
        name: LOCATION_SERVICE_MANAGMENT_ROUTE_NAME,
        params: { companyId, locationFiscalYearId },
      });
    },
    async onSetPhysicalAddress(address) {
      this.model.physicalAddressInfo = {
        id: null,
        addressTypeCd: AddressTypes.Physical,
        ...address,
      };
      await this.$refs[this.mainFormName].reset();
    },
    onAddOrEditContactHandler(model = null) {
      this.isOpenAddOrEditContact = true;
      this.showAddContact(model);
    },
    showAddContact(contact) {
      this.isOpenAddOrEditContact = true;
      if (contact) {
        this.model.secondaryContact = {
          id: contact.id,
          locationId: this.locationId,
          isPrimaryContactFlag: contact.isPrimaryContactFlag,
          name: contact.nameFirst,
          initial: contact.nameInitial,
          lastName: contact.nameLastPaternal,
          secondSurname: contact.nameLastMaternal,
          workDepartmentName: contact.department,
          workPositionTitle: contact.position,
          email: contact.email,
          telephoneMobileNo: contact.cellPhone,
          telephoneWorkNo: contact.workPhone,
          workPhoneExt: contact.workPhoneExt,
          telephoneWorkFaxNo: contact.fax,
          telephoneHomeNo: contact.homePhone,
        };
      }
    },
    saveAddorEditContact() {
      this.isOpenAddOrEditContact = false;
      this.model.secondaryContact = {
        locationId: null,
        isPrimaryContactFlag: null,
        name: null,
        initial: null,
        lastName: null,
        secondSurname: null,
        workDepartmentName: null,
        workPositionTitle: null,
        email: null,
        telephoneMobileNo: null,
        telephoneWorkNo: null,
        workPhoneExt: null,
        telephoneWorkFaxNo: null,
        telephoneHomeNo: null,
      };
      this.onLoad();
    },
    cancelAddorEditContact() {
      this.isOpenAddOrEditContact = false;
      this.model.secondaryContact = {
        locationId: null,
        isPrimaryContactFlag: null,
        name: null,
        initial: null,
        lastName: null,
        secondSurname: null,
        workDepartmentName: null,
        workPositionTitle: null,
        email: null,
        telephoneMobileNo: null,
        telephoneWorkNo: null,
        workPhoneExt: null,
        telephoneWorkFaxNo: null,
        telephoneHomeNo: null,
      };
    },
    async onDeleteContactHandler({ id }) {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar el contacto seleccionado?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      const { data } = await _deleteLocationServiceContact(id).catch(
        (error) => {
          this.ShowToast('Alerta', `${error.response.data.message}.`, 'error');
          return { data: false };
        }
      );

      if (data)
        this.ShowToast(
          `Confirmación`,
          'El contacto ha sido eliminado exitosamente.',
          'success'
        );

      this.onLoad();
    },
    async toggleSSNMaskHandler() {
      if (!this.hasUnMaskSSNPermission) return;

      const response = await _getSsn(this.companyId, !this.isSsnMasked);
      if (response?.data) {
        const ssn = response.data;
        this.requestInfo = {
          ...this.requestInfo,
          companyInfo: { ...this.requestInfo.companyInfo, ssn },
        };
        this.isSsnMasked = !this.isSsnMasked;
      }
    },
    onSelectedLocationType(locationType) {
      if (
        locationType.id === CodeLocationTypes.ProviderOffice ||
        locationType.id === CodeLocationTypes.School
      ) {
        this.isProviderOfficeOrSchool = true;
      } else {
        this.isProviderOfficeOrSchool = false;
      }
    },
    onSelectMunicipality(municipality) {
      this.municipalitySelected = municipality;
      this.model.physicalAddressInfo.municipalityId = municipality.id;
    },
    onSelectSchoolMunicipality(municipality) {
      this.municipalitySelected = municipality;
    },
    async onInactivateHandler() {
      const { data: validation } = await _hasactivespecialists(
        this.locationFiscalYearId
      ).catch((error) => {
        this.ShowToast('Alerta', `${error.response.data.message}`, 'error');
        return { data: false };
      });

      if (validation) {
        const answer = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro(a) que desea inactivar el lugar de servicio seleccionado?`,
          'Sí',
          'No'
        );
        if (!answer) return;

        const { data } = await _deactivateLocationService(
          this.locationFiscalYearId
        ).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });

        if (data) {
          this.onLoad();
          this.ShowToast(
            'Proceso Exitoso',
            'El lugar de servicio ha sido inactivado exitosamente.',
            'success'
          );
        }
      }
    },
    async onActiveHandler() {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro(a) que desea reactivar el lugar de servicio seleccionado?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      const { data } = await _activateLocationService(
        this.locationFiscalYearId
      ).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
      });

      if (data) {
        this.onLoad();
        this.ShowToast(
          'Proceso Exitoso',
          'El lugar de servicio ha sido reactivado exitosamente.',
          'success'
        );
      }
    },
  },
};
</script>
<style scoped>
@media screen and (min-width: 1200px) and (max-width: 1365px) {
  ::v-deep div.actions.mobile-actions.col-xl-4 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
</style>
